import React, {
  Dispatch,
  MouseEventHandler,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import CalculateInput from "./inputs/CalculateInput";
import ResultOutput from "./ResultOutput";
import {
  useElementSize,
  useIsMobile,
  useShowAds,
  useTextAlign,
  useTitle,
} from "../../lib/custom-hooks";
import { getPrices } from "../../lib/funcs";
import AdLinks from "./AdLinks";
import { ToastContainer } from "react-toastify";
import LanguageSelector from "../LanguageSelector";

interface ICalculatorProps {
  type: "margin" | "profit" | "lotsize" | "pipvalue";
  wide?: boolean;
  noLine?: boolean;
  children: ReactNode;
  result: IResult;
  calculate: MouseEventHandler<HTMLInputElement>;
  setInstruments: Dispatch<SetStateAction<IInstrument[]>>;
  setSelectedInstrument: Dispatch<SetStateAction<IInstrument | undefined>>;
  setDepositCurrencies: (i: IDepositCurrency[]) => void;
  setSelectedDepositCurrency: Dispatch<
    SetStateAction<IDepositCurrency | undefined>
  >;
  switches: ISwitch[];
  selectedInstrument: IInstrument | undefined;
  selectedDepositCurrency: IDepositCurrency | undefined;
  depositableCurrencies: IDepositCurrency[];
  setTradeSizeInfo: Dispatch<SetStateAction<ITradeSizeInfo | undefined>>;
  setLotUnits: Dispatch<SetStateAction<string | number>>;
}

function Calculator({
  noLine,
  children,
  result,
  calculate,
  setInstruments,
  setSelectedDepositCurrency,
  setSelectedInstrument,
  setDepositCurrencies,
  switches,
  selectedInstrument,
  selectedDepositCurrency,
  setTradeSizeInfo,
  depositableCurrencies,
  setLotUnits,
}: ICalculatorProps) {
  const [loading] = useState(false);
  const [wide, setWide] = useState(false);
  const elementSize = useElementSize();
  const showAds = useShowAds(['forex', 'crypto', '1']);
  const title = useTitle();
  const textAlign = useTextAlign();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (elementSize < 6) setWide(true);
    else setWide(false);
  }, [elementSize]);

  useEffect(() => {
    if (!depositableCurrencies.length)
      return setSelectedDepositCurrency(undefined);
    if (
      !selectedDepositCurrency ||
      !depositableCurrencies.filter(
        (dc) =>
          selectedDepositCurrency.GlobalCurrencyName === dc.GlobalCurrencyName
      ).length
    )
      setSelectedDepositCurrency(depositableCurrencies[0]);
  }, [selectedDepositCurrency, depositableCurrencies, setSelectedDepositCurrency]);

  useEffect(() => {
    if (!selectedInstrument) return;
    setLotUnits(selectedInstrument.LotSize);
    setTradeSizeInfo((tsi) => ({
      base: tsi?.base || "Lots",
      value: tsi?.base === "Units" ? selectedInstrument.LotSize : 1,
      unitsOf1Lot: selectedInstrument.LotSize,
    }));
  }, [selectedInstrument, setLotUnits, setTradeSizeInfo]);

  useEffect(() => {
    getPrices(false).then((data) => {
      if (data && data.success) {
        let defaultIns = "EURUSD";
        try {
          defaultIns = localStorage.getItem("selected-instrument") || "EURUSD";
        } catch (e) {}
        const currencyPairs = data.currencyPairs.map((cp: any) => ({
          ...cp,
          show: true,
          tradeSizeInfo: {
            value: 1,
            bases: { Lots: 1, Units: cp.LotSize },
            base: "Lots",
          },
        }));
        const si = currencyPairs.filter(
          (cp: IInstrument) => cp.DisplaySymbolName === defaultIns
        )[0];
        let defaultDep = "USD";
        try {
          defaultDep =
            localStorage.getItem("selected-deposit-currency") || "USD";
        } catch (e) {}
        const dc = data.currencyList.filter(
          (dl: IDepositCurrency) => dl.GlobalCurrencyName === defaultDep
        )[0];
        setInstruments(currencyPairs);
        setSelectedInstrument(si);
        setSelectedDepositCurrency(dc);
        setDepositCurrencies(data.currencyList);
      }
    });
  }, [setDepositCurrencies, setInstruments, setSelectedDepositCurrency, setSelectedInstrument]);

  useEffect(() => {
    try {
      localStorage.setItem(
        "selected-instrument",
        selectedInstrument?.DisplaySymbolName || "EURUSD"
      );
    } catch (e) {}
  }, [selectedInstrument]);

  useEffect(() => {
    try {
      localStorage.setItem(
        "selected-deposit-currency",
        selectedDepositCurrency?.GlobalCurrencyName || "USD"
      );
    } catch (e) {}
  }, [selectedDepositCurrency]);

  useEffect(() => {
    const calcEl = document.querySelector("#calculator") as HTMLDivElement;

    const sendSizeToParent = () => {
      const href = document.location.href.split("/");
      const calcType = href[href.length - 1].split("?")[0];
      const w = calcEl.clientWidth;
      const h = calcEl.clientHeight;
      window.top?.postMessage(`IFrameSize|${calcType}|${w}X${h}`, "*");
    };

    document.addEventListener("DOMContentLoaded", sendSizeToParent, false);
    new ResizeObserver(sendSizeToParent).observe(calcEl);
    return () => {
      new ResizeObserver(sendSizeToParent).unobserve(calcEl);
      document.removeEventListener("DOMContentLoaded", sendSizeToParent, false);
    };
  }, []);

  useEffect(() => {
    setInstruments((inst) => {
      const newInsts = inst.map((ins) => ({
        ...ins,
        show: switches.some((sw) => ins.Market === sw.label && sw.checked),
      }));
      setSelectedInstrument((si) => {
        const acceptableInstruments = newInsts
          .filter((ins) => ins.show)
          .filter(
            (ins) =>
              ins.DisplaySymbolName === si?.DisplaySymbolName ||
              ins.DisplaySymbolName === "EURUSD" ||
              ins.DisplaySymbolName === "BTCUSD"
          );
        return (
          acceptableInstruments.filter(
            (ai) => ai.DisplaySymbolName === si?.DisplaySymbolName
          )[0] || acceptableInstruments[0]
        );
      });
      return newInsts;
    });
  }, [setInstruments, setSelectedInstrument, switches]);
  const [windowInner, setWindowInner] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => {
      setWindowInner(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
  });

  return (
    <>
      <LanguageSelector />
      <div
        id="calculator"
        style={{
          width: windowInner < 768 ? "100%" : (wide ? "var(--wide-calculator-width)" : "var(--calculator-width)"),
          padding: windowInner < 768 ? "auto 2rem" : "",
        }}
        className={[
          noLine ? "no-borderline" : "",
          wide ? "wide" : "",
          isMobile ? "is-mobile" : "",
        ]
          .filter((c) => c.length)
          .join(" ")}
      >
        {title ? (
          <h3 style={{ textAlign: textAlign || "left" }}>{title}</h3>
        ) : null}
        {loading ? (
          <div id="spinner-container">
            <div id="loading-spinner" />
          </div>
        ) : (
          <div className="row">
            {children}
            <hr />
            <CalculateInput
              className={`col-${Math.min(elementSize * 2, 12)}`}
              calculate={calculate}
            />
            <ResultOutput
              vertical={elementSize === 6}
              className={`col-${Math.min(elementSize * 2, 12)}`}
              result={result}
            />
            {showAds ? <AdLinks /> : ""}
          </div>
        )}
        <ToastContainer />
      </div>
    </>
  );
}

export default Calculator;
